import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useAuthFlow = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  const createProfile = async (user: {
    id: string;
    email: string;
    user_metadata: { display_name?: string };
  }) => {
    try {
      if (!user.email) {
        console.error('Email is missing');
        throw new Error('Email is required');
      }

      const displayName = user.user_metadata.display_name || user.email.split('@')[0];
      console.log('Creating/updating profile with username:', displayName);
      
      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: user.email,
          username: displayName,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'id'
        });

      if (upsertError) {
        console.error('Profile upsert error:', upsertError);
        throw upsertError;
      }

      // Update user metadata if needed
      if (!user.user_metadata.display_name) {
        const { error: updateError } = await supabase.auth.updateUser({
          data: { display_name: displayName }
        });

        if (updateError) {
          console.error('User metadata update error:', updateError);
          // Don't throw here, as profile was created successfully
          toast.error('Failed to update display name');
        }
      }

      console.log('Profile created/updated successfully');
      return true;
    } catch (err) {
      console.error('Error in createProfile:', err);
      toast.error('Failed to create/update profile');
      throw err;
    }
  };

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const params = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
      
      const tokenHash = params.get('token_hash') || hashParams.get('token_hash');
      const type = params.get('type') || hashParams.get('type');

      if (tokenHash && type === 'signup') {
        setIsVerifying(true);
        try {
          const { error } = await supabase.auth.verifyOtp({
            type: 'signup',
            token_hash: tokenHash,
          });

          if (error) {
            setError('Email verification failed. Please try signing up again.');
            toast.error('Email verification failed');
            console.error('Email verification failed:', error.message);
          } else {
            await supabase.auth.signOut();
            toast.success('Email verified successfully! Please sign in.');
            window.history.replaceState({}, document.title, '/auth');
          }
        } catch (err) {
          console.error('Error during email verification:', err);
          setError('An unexpected error occurred during email verification.');
          toast.error('Email verification failed');
        } finally {
          setIsVerifying(false);
          setIsLoading(false);
        }
        return;
      }
      setIsLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          await createProfile({
            id: session.user.id,
            email: session.user.email!,
            user_metadata: session.user.user_metadata
          });
          navigate("/");
        } catch (err) {
          console.error("Profile creation error:", err);
          setError("Failed to create user profile");
          await supabase.auth.signOut();
        }
      }
    });

    handleEmailConfirmation();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  return { error, isLoading, isVerifying };
};